import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {GroupModel} from '../../../../../models/school/group.model';
import {ObjectiveFilter, ObjectiveModel} from '../../../../../models/edu-admin/objective.model';
import {SchoolAdminGroupService} from '../../../../../services/school/school-admin-group.service';
import {AdminObjectiveService} from '../../../../../services/admin/admin-objective.service';
import {SchoolModel} from '../../../../../models/school/school.model';

@Injectable({
  providedIn: 'root'
})
export class SchoolAdminGroupObjectiveChangeService {

  groupId: string;
  group: GroupModel;
  objectives: ObjectiveModel[] = [];

  currentObjective: ObjectiveModel | null = null;
  isLoadingCurrentObjective = false;
  isLoadingObjectives = false;

  isPickerOpen = false;
  isConfirming = false;
  pendingObjectiveId: string | null | undefined = undefined; // undefined = nothing picked, null = explicitly "None"
  pickerSearchQuery = '';
  confirmChecked = false;

  constructor(private groupSv: SchoolAdminGroupService, private objectiveSv: AdminObjectiveService) {}

  init(groupId: string, group: GroupModel) {
    this.groupId = groupId;
    this.group = group;
    this.currentObjective = null;

    if (group && group.objectiveId) {
      this.isLoadingCurrentObjective = true;
      const filter = new ObjectiveFilter();
      filter.populatePlacementTest = true;
      this.objectiveSv.fetchObjectiveById(group.objectiveId, filter).subscribe((obj) => {
        this.currentObjective = obj;
        this.isLoadingCurrentObjective = false;
      }, () => {
        this.isLoadingCurrentObjective = false;
      });
    }
  }

  getCurrentObjective(): ObjectiveModel {

    if (this.currentObjective) {
      return this.currentObjective;
    }

    return null;

  }

  getPendingObjective(): ObjectiveModel {

    if (!this.pendingObjectiveId) {
      return null;
    }

    for (const objective of this.objectives) {
      if (objective._id === this.pendingObjectiveId) {
        return objective;
      }
    }

    return null;

  }

  getFilteredPickerObjectives(): ObjectiveModel[] {

    if (!this.pickerSearchQuery || this.pickerSearchQuery.trim() === '') {
      return this.objectives;
    }

    const query = this.pickerSearchQuery.trim().toLowerCase();
    const filtered = [];

    for (const objective of this.objectives) {
      const name = objective.getTranslatedNameForUser() || objective.name || '';
      if (name.toLowerCase().indexOf(query) !== -1) {
        filtered.push(objective);
      }
    }

    return filtered;

  }

  getObjectiveAlerts(): { titleKey: string; messageKey: string }[] {

    const alerts = [];
    const obj = this.getCurrentObjective();

    if (!obj) {
      return alerts;
    }

    if (obj.isArchived) {
      alerts.push({
        titleKey: 'schoolAdministration.groups.objectiveArchivedAlertTitle',
        messageKey: 'schoolAdministration.groups.objectiveArchivedAlertMessage'
      });
    } else if (obj.placementTest && obj.placementTest.isArchived) {
      alerts.push({
        titleKey: 'schoolAdministration.groups.objectiveOldTestAlertTitle',
        messageKey: 'schoolAdministration.groups.objectiveOldTestAlertMessage'
      });
    }

    if (this.group && this.group.grade != null && obj.grade != null && this.group.grade !== obj.grade) {
      alerts.push({
        titleKey: 'schoolAdministration.groups.objectiveGradeMismatchAlertTitle',
        messageKey: 'schoolAdministration.groups.objectiveGradeMismatchAlertMessage'
      });
    }

    return alerts;

  }

  getCardState(): string {

    if (!this.group || !this.group.objectiveId) {
      return 'empty';
    }

    if (this.isPickerOpen) {
      return 'picking';
    }

    if (this.getObjectiveAlerts().length > 0) {
      return 'warn';
    }

    return 'ok';

  }

  openPicker() {
    this.isPickerOpen = true;
    this.pendingObjectiveId = undefined;
    this.pickerSearchQuery = '';

    if (this.objectives.length === 0 && !this.isLoadingObjectives) {
      this.isLoadingObjectives = true;
      this.objectiveSv.fetchPublicObjectives(SchoolModel.getCurrent().country, { populatePlacementTest: true }).subscribe((objectives) => {
        this.objectives = objectives;
        this.isLoadingObjectives = false;
      }, () => {
        this.isLoadingObjectives = false;
      });
    }
  }

  closePicker() {
    this.isPickerOpen = false;
    this.pendingObjectiveId = undefined;
    this.pickerSearchQuery = '';
  }

  selectPendingObjective(id: string | null) {
    this.pendingObjectiveId = id;
  }

  proceedToConfirm() {
    this.isPickerOpen = false;
    this.isConfirming = true;
    this.confirmChecked = false;
  }

  closeConfirm() {
    this.isConfirming = false;
    this.pendingObjectiveId = undefined;
  }

  confirmChange(): Observable<GroupModel> {
    return this.groupSv.updateGroupObjective(this.groupId, this.pendingObjectiveId);
  }

}
